// components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { StyledFlexMatchContainer } from './FlexMatch.styles';
import { FilterPopper } from './components/FilterPopper';
import { FlexMatchFilters } from './components/FlexMatchFilters';
import MatchDrawer from './components/MatchDrawer';
import CostCenterInfoDialog from './components/CostCenterInfoDialog';
import NewFlexRequestDrawer from './components/NewFlexRequestDrawer';
import FlexMatchSettingsDialog from '../match/FlexMatchSettingsDialog/FlexMatchSettingsDialog';
import PreferredEmployeesDialog from 'components/shared/PreferredEmployeesDialog';

// constants
import { FORM_DEFAULT_VALUES, VIEW_MODE_META } from './FlexMatch.consts';

// hooks
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetColumns } from './hooks/useGetColumns';
import { useGetCostCenters } from 'queries';
import { FormProvider, useForm } from 'react-hook-form';
import useGetFlexMatchTasks from './hooks/useGetFlexMatchTasks';
import useRejectFlexMatchRequest from './hooks/useRejectFlexMatchRequest';
import useAcceptFlexMatchRequest from './hooks/useAcceptFlexMatchRequest';
import {
  GridRowId,
  gridRowSelectionIdsSelector,
  GridValidRowModel,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { usePreferredEmployeeActions } from './hooks/usePreferredEmployeeActions';
import { useSelector } from 'hooks';

// stores
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// types
import { FlexMatchFilterFields } from './components/FlexMatchFilters.types';
import { FlexMatchFilterTypes } from './FlexMatch.types';
import { FlexMatchTaskResponse } from 'RestAPIs/flex-match/types';
import { PlanGroup } from 'components/types';
import { PreferredEmployee } from 'components/shared/PreferredEmployees.types';
import { PreferredEmployeeCreatedBy } from 'components/shared/PreferredEmployees.enums';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

// utils
import dayjs from 'dayjs';
import { computeRange } from 'components/shared/DateNavigation/DateNavigation.utils';
import { useFlexMatchSockets } from './hooks/useFlexMatchSockets';
import { SingleOrRange } from 'components/shared/DateNavigation';
import FeatureTour from 'components/shared/FeatureDiscovery/FeatureTour';
import { FlexMatchSteps } from './FeatureTour/flexMatch.steps';
import { FloatingSpeechBubbleButton } from 'components/shared/FeatureDiscovery/components/FloatingSpeechBubbleButton';
import { useQueryClient } from 'react-query';
import { FLEX_TASKS_QUERY_KEY } from './hooks/useFlexMatchSockets.consts';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';
import useAcceptAllFlexMatchRequest from './hooks/useAcceptAllFlexMatchRequest';
import { getToolbarActions } from './FlexMatch.utils';

const FlexMatch = () => {
  const { hasReactFlexMatchTableAccess } = useSelector((state) => state.grants);
  const apiRef = useGridApiRef();
  const isExternalAgencyAccount =
    sessionStorage.getItem('isExternalAgencyAccount') === 'true';
  const showProposed = !!isExternalAgencyAccount; // showProposed --> should also check whether hasFlexFeature
  // Note: not sure what hasFlexFeature in dojo is doing, there is no grant for it in the current codebase
  const { hasRegionalPoolOrganisationAccess } = useSelector(
    (state) => state.grants
  );
  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const { t: tHeader } = useTranslation('common', {
    keyPrefix: 'header'
  });
  const { data: costCenters = [] } = useGetCostCenters();
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('detailed');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore((state) => state);
  const { period } = useDateNavigationStore((state) => state);
  const computeDate = period === 'custom' ? selectedDateRange : selectedDate;
  const { startDate, endDate } = computeRange(computeDate, period);
  const [filters, setFilters] = useState<FlexMatchFilterTypes | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [matchDrawerOpen, setMatchDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FlexMatchTaskResponse | null>(
    null
  );
  const [rowSelectionMap, setRowSelectionMap] = useState<
    Map<GridRowId, GridValidRowModel>
  >(new Map());

  const [flexMatchSettingsDialogOpen, setFlexMatchSettingsDialogOpen] =
    useState(false);
  const [preferredEmployeesDialogOpen, setPreferredEmployeesDialogOpen] =
    useState(false);
  const { mutateAsync: rejectFlexMatchRequest } = useRejectFlexMatchRequest({
    isWebSocketUpdate: true
  });
  const { mutateAsync: acceptFlexMatchRequest } = useAcceptFlexMatchRequest({
    isWebSocketUpdate: true
  });
  const { mutateAsync: acceptAllFlexMatchRequest, isLoading: isAcceptingAll } =
    useAcceptAllFlexMatchRequest({
      isWebSocketUpdate: false
    });
  const handleInfoClick = useCallback((row: FlexMatchTaskResponse) => {
    setSelectedRow(row);
    setInfoOpen(true);
  }, []);

  const [selectedEmployees, setSelectedEmployees] = useState<
    PreferredEmployee[]
  >([]);

  const handleMatchDrawerClick = (row) => {
    setSelectedRow(row);
    setMatchDrawerOpen(true);
  };

  const { columns, defaultColumnVisibilityModel } = useGetColumns({
    costCenters,
    viewMode,
    onInfoClick: handleInfoClick
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    () => defaultColumnVisibilityModel
  );
  const fieldsSignature = useMemo(
    () => columns.map((c) => String(c.field)).join('|'),
    [columns]
  );
  const { addEmployeeFor, removeEmployeeFor } = usePreferredEmployeeActions();

  const handleAddEmployee = useMemo(
    () => addEmployeeFor(selectedRow, matchDrawerOpen),
    [addEmployeeFor, selectedRow, matchDrawerOpen]
  );
  const handleRemoveEmployee = useMemo(
    () => removeEmployeeFor(selectedRow, matchDrawerOpen),
    [removeEmployeeFor, selectedRow, matchDrawerOpen]
  );

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  const handleNewRequestClose = () => {
    setNewRequestOpen(false);
  };

  const handleMatchDrawerClose = () => {
    setMatchDrawerOpen(false);
    setSelectedRow(null);
  };

  const methods = useForm<FlexMatchFilterFields>({
    defaultValues: FORM_DEFAULT_VALUES
  });

  const onApplyFilters = methods.handleSubmit(
    (values: FlexMatchFilterFields) => {
      const costCenterUniqId = values.costCenter?.uniqId;
      const isCostCenterGroup =
        typeof costCenterUniqId === 'string' &&
        costCenterUniqId.includes('-group');

      const costCenterIds = isCostCenterGroup
        ? (values.costCenter as PlanGroup)?.costCenters?.map((cc) => cc.id)
        : values.costCenter?.id
        ? [values.costCenter.id]
        : null;

      const filterValues = {
        ...filters,
        agencyIds: values.agency?.id ? [values.agency?.id] : null,
        askedEmployee: values.employee?.empId ?? null,
        brIds: costCenterIds ? costCenterIds : null,
        clusterId: values.cluster?.clusterId ?? null,
        createdBy: values.preference?.value ? [values.preference.value] : null,
        flexPortalMatchFlags: values.status?.flexPortalMatchFlags
          ? values.status?.flexPortalMatchFlags
          : null,
        functionId: values.rosterFunction?.id ?? null,
        poolId: values.pool?.value ?? null,
        reason: values.reason?.name ?? null,
        taskDefinitionKey: values.status?.taskDefinitionKey ?? null
      };

      const validValues = Object.fromEntries(
        Object.entries(filterValues).filter(([, value]) => value != null)
      ) as FlexMatchFilterTypes;

      setFilters(validValues);
      handleClose();
    }
  );

  const onClearFilters = () => {
    methods.reset();
    setFilters({
      startDate,
      endDate
    });
  };

  const { data: flexMatchTasksData } = useGetFlexMatchTasks(
    {
      filters,
      page,
      pageSize
    },
    {
      enabled: !!filters,
      staleTime: matchDrawerOpen ? Infinity : 0
    }
  );

  const { data: rows = [], totalItems = 0 } = flexMatchTasksData || {};

  useFlexMatchSockets({
    enabled: true
  });

  const handleFilterClick = () => {
    setIsFilterOpen(true);
  };

  const handleClose = () => {
    setIsFilterOpen(false);
  };

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      if (period === 'custom') {
        setSelectedDateRange(Array.isArray(date) ? date : undefined);
      } else {
        setSelectedDate(date as Date | null);
      }
    },
    [period, setSelectedDate, setSelectedDateRange]
  );

  const handleAcceptRequest = (row) => {
    if (!row || !row.taskId) return;
    // Todo: add additional confirmation dialog for this action, this is in dojo not implemented though

    acceptFlexMatchRequest({
      taskId: row.taskId,
      body: {
        formdata: {
          actions: 'accept'
        }
      }
    });
  };

  const handleAcceptAllRequest = async () => {
    if (!rowSelectionMap || rowSelectionMap.size === 0) {
      return;
    }
    const taskIds = Array.from(rowSelectionMap.keys())
      .map((id) => apiRef.current?.getRow?.(id))
      .filter(Boolean)
      .map((row: any) => row.taskId)
      .filter(Boolean);
    if (taskIds.length === 0) return;

    await acceptAllFlexMatchRequest({
      body: { taskIds }
    });

    apiRef.current?.setRowSelectionModel?.({
      type: 'include',
      ids: new Set()
    });
  };

  const handleRejectRequest = (row) => {
    if (!row || !row.requestId || !row.processInstanceId) return;

    rejectFlexMatchRequest({
      body: {
        requestId: row.requestId,
        processInstanceId: row.processInstanceId
      }
    });
  };

  const handlePageChange = ({ page, pageSize }) => {
    setPage(page);
    setPageSize(pageSize);
  };

  const handlePreferredEmployees = (row) => {
    if (!row || !row.preferredEmployees) return;
    setSelectedRow(row);
    setSelectedEmployees(row.preferredEmployees);
    setPreferredEmployeesDialogOpen(true);
  };

  const handleFlexMatchSettingsDialogOpen = (row) => {
    setSelectedRow(row);
    setFlexMatchSettingsDialogOpen(true);
  };

  const handleFlexMatchSettingsDialogClose = () => {
    setSelectedRow(null);
    setFlexMatchSettingsDialogOpen(false);
  };

  const settingsRequests = useMemo(
    () => (selectedRow ? [selectedRow] : []),
    [selectedRow?.requestId, selectedRow?.date]
  );

  const queryClient = useQueryClient();

  const handleMatchSettingsSaved = async () => {
    await queryClient.invalidateQueries(FLEX_TASKS_QUERY_KEY);
  };

  // subscribe to row selection changes and update the rowSelectionMap state
  useEffect(() => {
    const unSub = apiRef.current?.subscribeEvent?.('rowSelectionChange', () =>
      setRowSelectionMap(gridRowSelectionIdsSelector(apiRef))
    );
    return () => unSub?.();
  }, []);

  useEffect(() => {
    if (!startDate || !endDate || !period) return;

    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate
    }));
  }, [startDate, endDate]);

  useEffect(() => {
    if (!!selectedRow) {
      const updatedRow = rows.find(
        (row) => row.requestId === selectedRow.requestId
      );
      updatedRow && setSelectedRow(updatedRow);
    }
  }, [rows]);

  // allow custom column visibility through the header
  useEffect(() => {
    setColumnVisibilityModel((prev) => {
      const same =
        Object.keys(prev).length ===
          Object.keys(defaultColumnVisibilityModel).length &&
        Object.keys(prev).every(
          (key) => prev[key] === defaultColumnVisibilityModel[key]
        );

      return same ? prev : defaultColumnVisibilityModel;
    });
  }, [viewMode, fieldsSignature]);

  if (!hasReactFlexMatchTableAccess) return null;

  return (
    <StyledFlexMatchContainer>
      <ToolbarWrapper
        actions={getToolbarActions({
          t,
          tHeader,
          filters,
          setNewRequestOpen,
          handleAcceptAllRequest,
          isAcceptingAll,
          rowSelectionMap,
          handleFilterClick,
          viewMode,
          setViewMode,
          VIEW_MODE_META
        })}
        toolbarDataTourId="flex-match-header"
        dateNavigation={{
          selectedDate: selectedDate ? dayjs(selectedDate).toDate() : null,
          selectedDateRange: selectedDateRange ? selectedDateRange : null,
          dateNavigationOptions: ['day', 'week', 'month', 'year', 'custom'],
          dateNavigationDataTourId: 'flex-match-date-navigation',
          allowDateNavigationMenuOptions: true,
          showDateNavigation: true,
          onSelectedDate: handleSelectedDateChange
        }}
        zenPinned={!!isFilterOpen}
      >
        <TableCRUDLayout
          apiRef={apiRef}
          getRowId={(row) => row.requestId}
          rows={rows ?? []}
          columns={columns}
          checkboxSelection
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          editable={false}
          page={page}
          isRowSelectableFromParent={(params) =>
            !!params.row?.actions?.some((a) => a?.accept)
          }
          pageSize={pageSize}
          rowCount={totalItems}
          isContentRangePagination
          onPaginationModelChange={handlePageChange}
          autosizeOnMount
          disableRowSelectionOnClick
          autosizeOptions={{
            columns: ['costCenterName'],
            includeHeaders: true,
            includeOutliers: true
          }}
          actionsThreshold={5}
          customHeight="100%"
          customActions={(_, row) => [
            {
              tooltipTitle: t('match'),
              icon: 'users' as IconType,
              onClick: () => handleMatchDrawerClick(row)
            },
            {
              tooltipTitle: t('approve'),
              icon: 'check' as IconType,
              onClick: () => handleAcceptRequest(row),
              hidden: !row?.actions?.some((action) => action.accept)
            },
            {
              tooltipTitle: t('reject'),
              icon: 'xmark' as IconType,
              onClick: () => handleRejectRequest(row),
              hidden: !row?.actions?.some((action) => action.reject)
            },
            {
              tooltipTitle: t('preference'),
              icon: 'star-outline' as IconType,
              onClick: () => handlePreferredEmployees(row)
            },
            {
              tooltipTitle: t('settings'),
              icon: 'gear' as IconType,
              onClick: () => handleFlexMatchSettingsDialogOpen(row)
            }
          ]}
        />
      </ToolbarWrapper>
      <FormProvider {...methods}>
        <FilterPopper
          isOpen={isFilterOpen}
          filterId="flex-match-filter"
          getAnchorEl={() => document.getElementById('flex-match-filter')}
          handleClose={handleClose}
          handleClearFilters={onClearFilters}
          handleApplyFilters={onApplyFilters}
          customZIndex={1200}
        >
          <FlexMatchFilters startDate={startDate} endDate={endDate} />
        </FilterPopper>
      </FormProvider>
      <CostCenterInfoDialog
        isOpen={infoOpen}
        onClose={handleInfoClose}
        selectedRow={selectedRow}
      />
      <NewFlexRequestDrawer
        isOpen={newRequestOpen}
        onClose={handleNewRequestClose}
      />
      <PreferredEmployeesDialog
        open={preferredEmployeesDialogOpen}
        onHidden={() => setPreferredEmployeesDialogOpen(false)}
        addEmployee={handleAddEmployee}
        removeEmployee={handleRemoveEmployee}
        employees={selectedEmployees}
        setSelectedEmployees={setSelectedEmployees}
        date={dayjs(selectedRow?.date).toDate()}
        createdBy={
          showProposed
            ? PreferredEmployeeCreatedBy.EXTERNAL_PLANNER
            : PreferredEmployeeCreatedBy.FLEX_PLANNER
        }
        showProposed={showProposed}
        dialogContainer={document.body}
      />
      <MatchDrawer
        isOpen={matchDrawerOpen}
        onClose={handleMatchDrawerClose}
        selectedRow={selectedRow}
        handleInfoClick={handleInfoClick}
        handleRejectRequest={handleRejectRequest}
        handlePreferredEmployees={handlePreferredEmployees}
      />
      {flexMatchSettingsDialogOpen && (
        <FlexMatchSettingsDialog
          open={flexMatchSettingsDialogOpen}
          onHidden={handleFlexMatchSettingsDialogClose}
          requests={settingsRequests}
          onSettingsSaved={handleMatchSettingsSaved}
          showRegionalPools={hasRegionalPoolOrganisationAccess}
          showAgencies={true} // Todo: not sure which grant this is!
          singleRowSettings
        />
      )}

      <FeatureTour
        tourId="flex-match-tour"
        tourVersion="1.0.0"
        desktopSteps={FlexMatchSteps}
        mobileSteps={FlexMatchSteps}
        triggerComponent={(start) => (
          <FloatingSpeechBubbleButton
            speechBubbleText={t('featureTour.speechBubbleText')}
            tooltipTitle={t('featureTour.tooltipTitle')}
            uniqueFeatureName="featureID-ReactFlexMatch"
            onClick={start}
          />
        )}
      />
    </StyledFlexMatchContainer>
  );
};

export default FlexMatch;
